import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import CloseIcon from 'mdi-react/CloseIcon';
import Loading from '../../shared/components/custom/Loading';
import EmptyStateInfo from '../../shared/components/custom/EmptyStateInfo';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import { partial } from '../../util/translation/translation';
import EmbedPreview from '../news/components/EmbedPreview';

const p = partial('AiBrandPage');

// As the content service checks them (ai-draft/brand.ts and its DTOs)
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const FONT_NAME = /^[\p{L}\p{N} -]{1,60}$/u;
const FONT_FILE_URL =
  /^https:\/\/[^\s"'()<>\\]+\.(woff2?|ttf|otf)(\?[^\s"'()<>\\]*)?$/i;
const HTTPS_URL = /^https:\/\/[^\s"'()<>\\]+$/i;
const MAX_TONE_LENGTH = 2000;
const MAX_BRAND_IMAGES = 5;
const MAX_IMAGE_DESCRIPTION = 150;
const MAX_STYLE_GUIDE_BYTES = 5 * 1024 * 1024;
const FONT_FORMATS = {
  woff2: 'woff2',
  woff: 'woff',
  ttf: 'truetype',
  otf: 'opentype',
};
// The shapes to pick from, as the CSS they become
const CORNER_STYLES = { square: '0', rounded: '12px', round: '24px' };
const BUTTON_STYLES = { square: '0', rounded: '10px', pill: '999px' };
const SHADOW_STYLES = { none: 'none', soft: '0 4px 16px rgba(0,0,0,0.08)' };

const COLOR_FIELDS = [
  ['primaryColor', '--club-primary'],
  ['accentColor', '--club-accent'],
  ['textColor', '--club-text'],
  ['backgroundColor', '--club-background'],
];
const LINK_FIELDS = [
  'ticketsUrl',
  'seasonTicketsUrl',
  'membershipUrl',
  'appUrl',
];
// Text fields and the length each may have
const TEXT_LENGTHS = {
  clubName: 100,
  stadiumName: 100,
  stadiumAddress: 200,
  supporterName: 80,
  slogans: 300,
  hashtags: 200,
  avoidWords: 500,
  tone: MAX_TONE_LENGTH,
};
const EMPTY = {
  primaryColor: '',
  accentColor: '',
  textColor: '',
  backgroundColor: '',
  headingFontFamily: '',
  headingFontUrl: '',
  fontFamily: '',
  fontUrl: '',
  cornerStyle: '',
  buttonStyle: '',
  shadowStyle: '',
  logoUrl: '',
  brandImages: [],
  clubName: '',
  stadiumName: '',
  stadiumAddress: '',
  websiteUrl: '',
  ticketsUrl: '',
  seasonTicketsUrl: '',
  membershipUrl: '',
  appUrl: '',
  tone: '',
  supporterName: '',
  slogans: '',
  hashtags: '',
  avoidWords: '',
  formality: '',
  emoji: '',
};

const isValid = (value, pattern) => !value || pattern.test(value.trim());

// The errors to show, by field ({} when all is well)
const validate = (values) => {
  const errors = {};
  COLOR_FIELDS.forEach(([field]) => {
    if (!isValid(values[field], HEX_COLOR)) errors[field] = p('invalidColor');
  });
  ['fontFamily', 'headingFontFamily'].forEach((field) => {
    if (!isValid(values[field], FONT_NAME)) {
      errors[field] = p('invalidFontName');
    }
  });
  ['fontUrl', 'headingFontUrl'].forEach((field) => {
    if (!isValid(values[field], FONT_FILE_URL)) {
      errors[field] = p('invalidFontUrl');
    }
  });
  if (!isValid(values.logoUrl, HTTPS_URL)) {
    errors.logoUrl = p('invalidLogoUrl');
  }
  ['websiteUrl', ...LINK_FIELDS].forEach((field) => {
    if (!isValid(values[field], HTTPS_URL)) errors[field] = p('invalidLink');
  });
  Object.entries(TEXT_LENGTHS).forEach(([field, max]) => {
    if (values[field].length > max) {
      errors[field] = p('tooLong', { max });
    }
  });
  values.brandImages.forEach((image, index) => {
    if (image.description.length > MAX_IMAGE_DESCRIPTION) {
      errors[`brandImages.${index}`] = p('tooLong', {
        max: MAX_IMAGE_DESCRIPTION,
      });
    }
  });
  return errors;
};

const fontFace = (family, url) =>
  family && url
    ? `@font-face{font-family:'${family}';src:url(${url}) format('${
        FONT_FORMATS[url.match(FONT_FILE_URL)[1].toLowerCase()]
      }');font-display:swap}`
    : '';

// The club's look as the content service puts it in front of a page
const brandCss = (values) => {
  const valid = (field, pattern) =>
    values[field] && pattern.test(values[field].trim())
      ? values[field].trim()
      : null;
  const fontFamily = valid('fontFamily', FONT_NAME);
  const fontUrl = valid('fontUrl', FONT_FILE_URL);
  const headingFontFamily = valid('headingFontFamily', FONT_NAME);
  const headingFontUrl = valid('headingFontUrl', FONT_FILE_URL);
  const variables = [
    ...COLOR_FIELDS.map(([field, name]) => {
      const color = valid(field, HEX_COLOR);
      return color && `${name}:${color}`;
    }).filter(Boolean),
    ...(fontFamily ? [`--club-font:'${fontFamily}',sans-serif`] : []),
    ...(headingFontFamily
      ? [`--club-heading-font:'${headingFontFamily}',sans-serif`]
      : []),
    ...(CORNER_STYLES[values.cornerStyle]
      ? [`--club-radius:${CORNER_STYLES[values.cornerStyle]}`]
      : []),
    ...(BUTTON_STYLES[values.buttonStyle]
      ? [`--club-button-radius:${BUTTON_STYLES[values.buttonStyle]}`]
      : []),
    ...(SHADOW_STYLES[values.shadowStyle]
      ? [`--club-shadow:${SHADOW_STYLES[values.shadowStyle]}`]
      : []),
  ];
  return `${fontFace(fontFamily, fontUrl)}${
    headingFontFamily !== fontFamily || headingFontUrl !== fontUrl
      ? fontFace(headingFontFamily, headingFontUrl)
      : ''
  }${variables.length ? `.club-page{${variables.join(';')}}` : ''}`;
};

const escape = (text) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

// A small page in the style the pages are told to use, to see the look
const previewHtml = (values) => {
  const https = (value) =>
    value && HTTPS_URL.test(value.trim()) ? value.trim() : null;
  const logoUrl = https(values.logoUrl);
  const photo = https(values.brandImages[0]?.url);
  return `<style>${brandCss(values)}
.club-page{font-family:var(--club-font,inherit);color:var(--club-text,#1f2933);background:var(--club-background,#ffffff);padding:20px 16px;font-size:16px}
.club-page .logo{display:block;height:40px;width:auto;margin:0 0 12px}
.club-page .card{margin:0 0 16px;padding:16px;border-radius:var(--club-radius,12px);box-shadow:var(--club-shadow,none);border:1px solid rgba(0,0,0,0.06);background:var(--club-background,#ffffff)}
.club-page .photo{display:block;width:100%;height:120px;object-fit:cover;border-radius:var(--club-radius,12px);margin:0 0 12px}
.club-page .tag{display:inline-block;margin:0 0 8px;padding:2px 10px;border-radius:999px;background:var(--club-accent,#1a7f45);color:#ffffff;font-size:12px;font-weight:600}
.club-page h2{margin:0 0 8px;font-family:var(--club-heading-font,var(--club-font,inherit));font-size:28px;line-height:1.15;color:var(--club-primary,#1f2933)}
.club-page p{margin:0 0 16px;line-height:1.5}
.club-page .button{display:block;padding:14px 18px;border-radius:var(--club-button-radius,10px);background:var(--club-primary,#1f2933);color:var(--club-background,#ffffff);font-family:var(--club-heading-font,var(--club-font,inherit));font-weight:600;text-align:center}
</style><div class="club-page">${
    logoUrl ? `<img class="logo" src="${escape(logoUrl)}" alt="">` : ''
  }<div class="card">${
    photo ? `<img class="photo" src="${escape(photo)}" alt="">` : ''
  }<span class="tag">${escape(p('previewTag'))}</span><h2>${escape(
    p('previewHeading')
  )}</h2><p>${escape(p('previewText'))}</p><span class="button">${escape(
    p('previewButton')
  )}</span></div></div>`;
};

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;

const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  const { data } = await Api.images.uploadImage(formData);
  return data.url;
};

/**
 * The club's look and voice for pages written with AI: colours, fonts,
 * shapes, logo and photos, facts and links pages may use, how the club
 * writes, and its style guide, with a preview of the look. Saved in the
 * content service, which puts the look in front of every page and tells
 * the AI the rest. Club facts start from the club's settings.
 */
const AiBrandPage = () => {
  const config = useConfig();
  const [values, setValues] = useState(EMPTY);
  const [styleGuide, setStyleGuide] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Club facts filled in from the club's settings, not yet saved
  const [prefilled, setPrefilled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const errors = useMemo(() => validate(values), [values]);
  const preview = useMemo(() => previewHtml(values), [values]);

  // Whether the club has AI pages decides what's shown; a style that can't
  // be loaded is a separate problem, not "not turned on"
  useEffect(() => {
    const load = async () => {
      let usage;
      try {
        usage = await Api.news.aiDraftUsage();
      } catch {
        setEnabled(false);
        return;
      }
      setEnabled(!!usage?.enabled);
      if (!usage?.enabled) return;
      let brand;
      try {
        brand = await Api.news.getAiBrand();
      } catch {
        setLoadFailed(true);
        return;
      }
      const loaded = Object.fromEntries(
        Object.entries(EMPTY).map(([field, empty]) => [
          field,
          brand?.[field] || empty,
        ])
      );
      // What the club's settings already know, where nothing is saved
      const fromConfig = {
        clubName: config.club_name,
        stadiumName: config.stadium_name,
        stadiumAddress: config.stadium_address,
        // Only an address the content service takes
        logoUrl: HTTPS_URL.test(config.club_logo || '') ? config.club_logo : '',
      };
      let filled = false;
      Object.entries(fromConfig).forEach(([field, value]) => {
        if (!loaded[field] && typeof value === 'string' && value.trim()) {
          loaded[field] = value.trim();
          filled = true;
        }
      });
      setValues(loaded);
      setPrefilled(filled);
      setStyleGuide(brand?.styleGuide || null);
    };
    load().finally(() => setLoading(false));
    // config is read as it is when the page opens
    // eslint-disable-next-line
  }, []);

  const set = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  const setImages = (update) =>
    setValues((prev) => ({ ...prev, brandImages: update(prev.brandImages) }));

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading('logo');
    try {
      const url = await uploadImage(file);
      setValues((prev) => ({ ...prev, logoUrl: url }));
    } catch (err) {
      toast.error(String(err));
    }
    setUploading(null);
  };

  const uploadPhotos = async (event) => {
    const files = [...(event.target.files || [])].slice(
      0,
      MAX_BRAND_IMAGES - values.brandImages.length
    );
    event.target.value = '';
    if (!files.length) return;
    setUploading('photos');
    // One at a time, so the order they were picked in is kept
    for (const file of files) {
      try {
        const url = await uploadImage(file);
        setImages((images) => [...images, { url, description: '' }]);
      } catch (err) {
        toast.error(String(err));
      }
    }
    setUploading(null);
  };

  const uploadStyleGuide = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_STYLE_GUIDE_BYTES) {
      toast.error(p('styleGuideTooLarge'));
      return;
    }
    setUploading('styleGuide');
    try {
      const saved = await Api.news.uploadAiStyleGuide(
        file.name,
        await readAsBase64(file)
      );
      setStyleGuide(saved);
      toast.success(p('styleGuideSaved'));
    } catch (err) {
      toast.error(String(err));
    }
    setUploading(null);
  };

  const removeStyleGuide = async () => {
    try {
      await Api.news.removeAiStyleGuide();
      setStyleGuide(null);
    } catch (err) {
      toast.error(String(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(
        Object.entries(values).map(([field, value]) => [
          field,
          typeof value === 'string' ? value.trim() || null : value,
        ])
      );
      body.brandImages = values.brandImages.map((image) => ({
        url: image.url,
        description: image.description.trim(),
      }));
      await Api.news.updateAiBrand(body);
      setPrefilled(false);
      toast.success(p('saved'));
    } catch (err) {
      toast.error(String(err));
    }
    setSaving(false);
  };

  const errorOrHint = (name, hint) =>
    errors[name] ? (
      <span className="form__form-group-error">{errors[name]}</span>
    ) : (
      hint && <span className="ai-brand__hint">{hint}</span>
    );

  const field = (name, { hint, placeholder, multiline } = {}) => (
    <div className="form__form-group">
      <label className="form-header" htmlFor={`ai-brand-${name}`}>
        {p(name)}
      </label>
      {multiline ? (
        <textarea
          id={`ai-brand-${name}`}
          className="ai-brand__input"
          rows={multiline}
          value={values[name]}
          placeholder={placeholder}
          onChange={set(name)}
        />
      ) : (
        <input
          id={`ai-brand-${name}`}
          className="ai-brand__input"
          value={values[name]}
          placeholder={placeholder}
          onChange={set(name)}
        />
      )}
      {errorOrHint(name, hint)}
    </div>
  );

  const choice = (name, options) => (
    <div className="form__form-group">
      <label className="form-header" htmlFor={`ai-brand-${name}`}>
        {p(name)}
      </label>
      <select
        id={`ai-brand-${name}`}
        className="ai-brand__input"
        value={values[name]}
        onChange={set(name)}
      >
        <option value="">{p('choiceNone')}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {p(`${name}-${option}`)}
          </option>
        ))}
      </select>
    </div>
  );

  if (loading) {
    return <Loading loading />;
  }

  return (
    <Container>
      <h3 className="page-title">{p('title')}</h3>
      {!enabled || loadFailed ? (
        <Card>
          <CardBody>
            <EmptyStateInfo
              icon="layout"
              title={p('title')}
              text={loadFailed ? p('loadFailed') : p('notEnabled')}
            />
          </CardBody>
        </Card>
      ) : (
        <Row>
          <Col lg={7}>
            <Card>
              <CardBody>
                <p className="ai-brand__intro">{p('intro')}</p>

                <h4 className="ai-brand__heading">{p('club')}</h4>
                {prefilled && (
                  <p className="ai-brand__notice">{p('prefilled')}</p>
                )}
                {field('clubName')}
                <div className="ai-brand__pair">
                  {field('stadiumName')}
                  {field('stadiumAddress')}
                </div>
                {field('websiteUrl', { placeholder: 'https://' })}

                <h4 className="ai-brand__heading">{p('links')}</h4>
                <p className="ai-brand__hint">{p('linksHint')}</p>
                <div className="ai-brand__pair">
                  {LINK_FIELDS.map((name) => (
                    <React.Fragment key={name}>
                      {field(name, { placeholder: 'https://' })}
                    </React.Fragment>
                  ))}
                </div>

                <h4 className="ai-brand__heading">{p('colors')}</h4>
                <div className="ai-brand__colors">
                  {COLOR_FIELDS.map(([name]) => (
                    <div className="form__form-group" key={name}>
                      <label
                        className="form-header"
                        htmlFor={`ai-brand-${name}`}
                      >
                        {p(name)}
                      </label>
                      <div className="ai-brand__color">
                        <input
                          type="color"
                          aria-label={p(name)}
                          value={
                            HEX_COLOR.test(values[name])
                              ? values[name]
                              : '#ffffff'
                          }
                          onChange={set(name)}
                        />
                        <input
                          id={`ai-brand-${name}`}
                          className="ai-brand__input"
                          value={values[name]}
                          placeholder="#145583"
                          onChange={set(name)}
                        />
                      </div>
                      {errorOrHint(name)}
                    </div>
                  ))}
                </div>

                <h4 className="ai-brand__heading">{p('fonts')}</h4>
                <div className="ai-brand__pair">
                  {field('headingFontFamily', { placeholder: 'Bebas Neue' })}
                  {field('headingFontUrl', {
                    hint: p('fontUrlHint'),
                    placeholder: 'https://',
                  })}
                  {field('fontFamily', { placeholder: 'Inter' })}
                  {field('fontUrl', {
                    hint: p('fontUrlHint'),
                    placeholder: 'https://',
                  })}
                </div>

                <h4 className="ai-brand__heading">{p('shapes')}</h4>
                <div className="ai-brand__pair ai-brand__pair--three">
                  {choice('cornerStyle', Object.keys(CORNER_STYLES))}
                  {choice('buttonStyle', Object.keys(BUTTON_STYLES))}
                  {choice('shadowStyle', Object.keys(SHADOW_STYLES))}
                </div>

                <h4 className="ai-brand__heading">{p('logo')}</h4>
                {field('logoUrl', { placeholder: 'https://' })}
                <label className="btn btn-outline-secondary btn-sm mb-3">
                  {uploading === 'logo' ? p('uploading') : p('uploadLogo')}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={!!uploading}
                    onChange={uploadLogo}
                  />
                </label>

                <h4 className="ai-brand__heading">{p('photos')}</h4>
                <p className="ai-brand__hint">
                  {p('photosHint', { max: MAX_BRAND_IMAGES })}
                </p>
                {values.brandImages.map((image, index) => (
                  <div className="ai-brand__photo" key={image.url}>
                    <img src={image.url} alt="" />
                    <div className="ai-brand__photo-text">
                      <input
                        className="ai-brand__input"
                        aria-label={p('photoDescription')}
                        value={image.description}
                        placeholder={p('photoDescription')}
                        onChange={(event) => {
                          const description = event.target.value;
                          setImages((images) =>
                            images.map((other, at) =>
                              at === index ? { ...other, description } : other
                            )
                          );
                        }}
                      />
                      {errorOrHint(`brandImages.${index}`)}
                    </div>
                    <button
                      type="button"
                      className="ai-brand__photo-remove"
                      aria-label={p('removePhoto')}
                      title={p('removePhoto')}
                      onClick={() =>
                        setImages((images) =>
                          images.filter((_, at) => at !== index)
                        )
                      }
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                ))}
                {values.brandImages.length < MAX_BRAND_IMAGES && (
                  <label className="btn btn-outline-secondary btn-sm mb-3">
                    {uploading === 'photos'
                      ? p('uploading')
                      : p('uploadPhotos')}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      disabled={!!uploading}
                      onChange={uploadPhotos}
                    />
                  </label>
                )}

                <h4 className="ai-brand__heading">{p('voice')}</h4>
                <div className="form__form-group">
                  <label className="form-header" htmlFor="ai-brand-tone">
                    {p('toneHint')}
                  </label>
                  <textarea
                    id="ai-brand-tone"
                    className="ai-brand__input"
                    rows={5}
                    value={values.tone}
                    placeholder={p('tonePlaceholder')}
                    onChange={set('tone')}
                  />
                  {errorOrHint(
                    'tone',
                    `${values.tone.length} / ${MAX_TONE_LENGTH}`
                  )}
                </div>
                <div className="ai-brand__pair">
                  {field('supporterName', {
                    placeholder: p('supporterNamePlaceholder'),
                  })}
                  {field('hashtags', { placeholder: '#' })}
                </div>
                {field('slogans', { multiline: 2 })}
                {field('avoidWords', {
                  multiline: 2,
                  hint: p('avoidWordsHint'),
                })}
                <div className="ai-brand__pair">
                  {choice('formality', ['informal', 'formal'])}
                  {choice('emoji', ['yes', 'no'])}
                </div>

                <Button
                  type="button"
                  color="primary"
                  className="mb-0"
                  disabled={
                    saving || !!uploading || Object.keys(errors).length > 0
                  }
                  onClick={save}
                >
                  {p('save')}
                </Button>
              </CardBody>
            </Card>

            {/* Saved on upload, apart from the fields above */}
            <Card>
              <CardBody>
                <h4 className="ai-brand__heading">{p('styleGuide')}</h4>
                <p className="ai-brand__hint">{p('styleGuideHint')}</p>
                {styleGuide ? (
                  <div className="ai-brand__file">
                    <span>
                      <strong>{styleGuide.name}</strong>{' '}
                      {formatSize(styleGuide.size)}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      outline
                      color="danger"
                      className="mb-0"
                      onClick={removeStyleGuide}
                    >
                      {p('removeStyleGuide')}
                    </Button>
                  </div>
                ) : null}
                <label className="btn btn-outline-secondary btn-sm mb-0">
                  {uploading === 'styleGuide'
                    ? p('uploading')
                    : styleGuide
                      ? p('replaceStyleGuide')
                      : p('uploadStyleGuide')}
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    disabled={!!uploading}
                    onChange={uploadStyleGuide}
                  />
                </label>
              </CardBody>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="ai-brand__preview-card">
              <CardBody>
                <h4 className="ai-brand__heading">{p('preview')}</h4>
                <div className="ai-brand__preview">
                  <EmbedPreview html={preview} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default AiBrandPage;
